import { bindActionCreators } from '@reduxjs/toolkit';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { validateBooleanQuery } from '~/api/search';
import { evisortBlue } from '~/assets/shared-styles/general';
import { togglePinFilterField } from '~/components/AlertsPage/AlertsPage.data';
import AnalyzerFilters from '~/components/AnalyzerPage/AnalyzerFilters/AnalyzerFilters';
import AnalyzerForm from '~/components/AnalyzerPage/AnalyzerForm/AnalyzerForm';
import {
  deleteSavedQuery,
  getAnalyzerFiltersData,
  getAnalyzerSearchQueries,
  getSavedQueries,
  renameSavedQuery,
} from '~/components/AnalyzerPage/AnalyzerPage.data';
import {
  preprocessQueryData,
  updateFilterValues,
} from '~/components/AnalyzerPage/AnalyzerPage.utils';
import AnalyzersQueryTable from '~/components/AnalyzerPage/AnalyzersQueryTable/AnalyzersQueryTable';
import { updateFilterPinnedProperty } from '~/components/Modals/AddFilterModal/AddFilterModal.utils';
import { validateFiltersForm } from '~/components/Modals/AlertModal/AlertModal.utils';
import {
  checkSearchQueryHasUnsupportedEntity,
  testIsSearchV3ComplexQuery,
} from '~/components/SearchV2/utils';
import { showToast } from '~/components/Shared/EcToast/showToast';
import ExpiringIcon from '~/components/Shared/Icons/ExpiringIcon';
import InboxIcon from '~/components/Shared/Icons/InboxIcon';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { convertQueryV2ToQueryV3 } from '~/features/search/utils/convertQueryV2ToQueryV3';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { testHasFlag } from '~/permissions/utils/testHasFlag';
import {
  testIsSupportedFilterType,
  testIsSupportedSearchV3FilterType,
  toFilters,
} from '~/redux/api/methods/search';
import { transformFieldSections } from '~/redux/api/transformers/search';
import { slices } from '~/redux/slices';
import search from '~/redux/slices/search';
import { withRouting } from '~/routing/withRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';

class Page extends Component {
  constructor(props) {
    super(props);

    this.handleSavedSearchesPageChange =
      this.handleSavedSearchesPageChange.bind(this);

    this.state = {
      loadingAnalyzerFields: true,
      loadingAnalyzerFieldsError: false,
      loadingAnalyzerQueries: true,
      loadingAnalyzerQueriesError: false,
      filters: [],
      filterOptions: [],
      provisionOptions: [],
      recentQueries: [],
      savedQueries: [],
      savedQueriesCount: 0,
      booleanQuery: '',
    };
  }

  componentDidMount() {
    const { flags, navigate } = this.props;
    if (flags[FlagType.DeprecateAnalyzer]) {
      setTimeout(() => {
        navigate('/search');
      }, 250);
    }
    this.loadAnalyzerFiltersData();
    this.loadAnalyzerSearchQueries();
  }

  componentWillUnmount() {
    const { analyzerResetData, location } = this.props;

    if (
      location.pathname !== '/analyzer/results' &&
      location.pathname !== '/analyzer'
    ) {
      analyzerResetData();
    }
  }

  loadAnalyzerSearchQueries = () => {
    this.setState({
      loadingAnalyzerQueries: true,
      loadingAnalyzerQueriesError: false,
    });

    getAnalyzerSearchQueries(
      (recentQueries, savedQueries, savedQueriesCount) =>
        this.setState({
          recentQueries,
          savedQueries,
          savedQueriesCount,
          loadingAnalyzerQueries: false,
        }),
      () =>
        this.setState({
          loadingAnalyzerQueries: false,
          loadingAnalyzerQueriesError: true,
        }),
    );
  };

  loadAnalyzerFiltersData = () => {
    const { filters } = this.props;

    this.setState({
      loadingAnalyzerFields: true,
      loadingAnalyzerFieldsError: false,
    });

    getAnalyzerFiltersData(
      (alertSectionsResp, provisionOptions) => {
        // Current workaround for 'Document Id' field

        const newSections = alertSectionsResp.sections.map((sec) => {
          if (sec.title === 'Other Fields') {
            return { ...sec };
          }
          return sec;
        });

        function removeFieldValues(sections) {
          return sections.map((section) => {
            section.fields = section.fields.map((field) => {
              delete field.values;
              return field;
            });
            return section;
          });
        }

        // this is to update the filters with section data array "values" suggestions
        const allFields = newSections.reduce((array, section) => {
          return array.concat(section.fields);
        }, []);
        const newFilters = updateFilterValues(allFields, filters);

        // set filters
        this.setState({
          fieldSections: transformFieldSections(alertSectionsResp),
          filters: newFilters,
          filterOptions: removeFieldValues(newSections),
          provisionOptions: provisionOptions.suggestions,
          loadingAnalyzerFields: false,
        });
      },
      () =>
        this.setState({
          loadingAnalyzerFields: false,
          loadingAnalyzerFieldsError: true,
        }),
    );
  };

  handleSavedSearchesPageChange(page) {
    getSavedQueries(page).then((savedQueries) => {
      this.setState({
        savedQueries: savedQueries.results,
        savedQueriesCount: savedQueries.count,
      });
    });
  }

  handleSearchFilters = (search) => {
    const { analyzerSetData, navigate } = this.props;

    analyzerSetData({ filters: search.query });
    navigate('/analyzer/results');
  };

  handleUpdateFilters = (filters, scrollToLast) => {
    // set filters
    this.setState({ filters }, () => {
      if (scrollToLast)
        document
          .getElementById('analyzerFormActions')
          .scrollIntoView({ block: 'center' });
      this.handleUpdateBooleanQuery('');
    });
  };

  handleUpdateFiltersFromTableQuery = (filters, scrollToLast) => {
    const { filterOptions } = this.state;
    const allFields = filterOptions.reduce((acc, val) => {
      return acc.concat(val.fields);
    }, []);
    const newFilters = updateFilterValues(allFields, filters);

    this.handleUpdateFilters(newFilters, scrollToLast);
  };

  handleUpdateFilterOptions = (filterId) => {
    this.updateFilterOptionPinned(filterId);

    togglePinFilterField(filterId).catch(() => {
      showToast(
        ERROR,
        this.props.intl.formatMessage({
          id: 'AnalyzerPage.pinFilterError',
          defaultMessage: "There's been an error with pinning this filter.",
          description: 'Error message when pinning a filter fails',
        }),
      );
      this.updateFilterOptionPinned(filterId); // revert the toggle if failed
    });
  };

  handleUpdateBooleanQuery = (booleanQuery) => {
    this.setState({ booleanQuery });
  };

  updateFilterOptionPinned(filterId) {
    const { filterOptions } = this.state;

    const newFilterOptions = updateFilterPinnedProperty(
      filterId,
      filterOptions,
    );
    this.updateFilterOptions(newFilterOptions);
  }

  updateFilterOptions(filterOptions) {
    this.setState({ filterOptions });
  }

  runSearchInAdvancedSearch() {
    const { dispatch, flags } = this.props;
    const { booleanQuery, fieldSections, filters } = this.state;
    const isComplexSearch = flags[FlagType.SearchV3]
      ? testIsSearchV3ComplexQuery(filters)
      : checkSearchQueryHasUnsupportedEntity(filters);

    dispatch(slices.searchV3.actions.reset());
    dispatch(search.actions.reset());

    if (isComplexSearch) {
      dispatch(search.actions.setUnsupportedQuery(filters));
      dispatch(
        slices.searchV3.actions.setQueryBuilder(
          convertQueryV2ToQueryV3(filters),
        ),
      );
    } else {
      const supportedFieldEntities = filters.filter(
        flags[FlagType.SearchV3]
          ? testIsSupportedSearchV3FilterType
          : testIsSupportedFilterType,
      );
      const filtersSupported = toFilters(supportedFieldEntities);

      dispatch(search.actions.setBooleanQuery(booleanQuery));
      dispatch(search.actions.setFilters(filtersSupported));

      dispatch(slices.searchV3.actions.setSearchText(booleanQuery));
      dispatch(slices.searchV3.actions.setSelectedFilters(filtersSupported));

      dispatch(
        search.actions.setQuery({
          booleanQuery,
          filters: filtersSupported,
          fields: fieldSections,
        }),
      );
    }

    dispatch(slices.searchV3.actions.setPage(1));
    dispatch(search.actions.setPage(1));

    this.props.navigate('/search');
  }

  handleSubmit = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const { analyzerSetData, currentUser, flags, navigate, intl, formatDate } =
      this.props;
    const { filters, booleanQuery } = this.state;
    const { filtersFormValid, validatedFilters } = validateFiltersForm(filters);

    const hasSearchV2Flag = testHasFlag(FeatureFlagType.SearchV2)(currentUser);
    const shouldRedirectToSearch =
      hasSearchV2Flag && flags[FlagType.SearchPageResultsForAnalyzer];

    validateBooleanQuery(booleanQuery)
      .then(() => {
        if (filtersFormValid) {
          if (shouldRedirectToSearch) {
            this.runSearchInAdvancedSearch();
          } else {
            const requestFilters = filters.length
              ? filters
              : [{ entity: 'bool_text_search', query: booleanQuery }];

            analyzerSetData({
              filters: preprocessQueryData(requestFilters, formatDate),
            });
            navigate('/analyzer/results');
          }
        } else {
          // set filters
          this.setState({ filters: validatedFilters });
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'AnalyzerPage.filtersValidationError',
              defaultMessage:
                'Please make sure that none of the sections or selected filters are blank or not properly set.',
              description: 'Error message when form validation fails',
            }),
          );
        }
      })
      .catch(() => {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AnalyzerPage.booleanQueryValidationError',
            defaultMessage:
              'An error occurred while validating a boolean expression.',
            description: 'Error message when boolean query validation fails',
          }),
        );
      });
  };

  handleQueryDelete = (itemToDelete) => {
    const { intl } = this.props;
    const successMessage = intl.formatMessage(
      {
        id: 'AnalyzerPage.queryDeleteSuccess',
        defaultMessage:
          '"{queryName}" search query has been successfully deleted.',
        description:
          'Success message when a query is deleted. Example: queryName = "My Search"',
      },
      {
        queryName: itemToDelete.name,
      },
    );

    deleteSavedQuery(itemToDelete.id)
      .then(() => {
        this.handleSavedSearchesPageChange(1);
        showToast(SUCCESS, successMessage);
      })
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AnalyzerPage.queryDeleteError',
            defaultMessage: 'Something went wrong with query deletion.',
            description: 'Error message when query deletion fails',
          }),
        ),
      );
  };

  handleQueryRename = (itemToRename, newItemName) => {
    const { intl } = this.props;
    const { savedQueries } = this.state;
    const successMessage = intl.formatMessage(
      {
        id: 'AnalyzerPage.queryRenameSuccess',
        defaultMessage:
          '"{queryName}" search query has been successfully renamed.',
        description:
          'Success message when a query is renamed. Example: queryName = "My Search"',
      },
      {
        queryName: itemToRename.name,
      },
    );

    renameSavedQuery(itemToRename.id, newItemName)
      .then(() => {
        const newResults = savedQueries.map((item) => {
          if (item.id === itemToRename.id) {
            item.name = newItemName;
            return item;
          }
          return item;
        });

        this.setState({ savedQueries: newResults });
        showToast(SUCCESS, successMessage);
      })
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AnalyzerPage.queryRenameError',
            defaultMessage: 'Something went wrong with query rename.',
            description: 'Error message when query rename fails',
          }),
        ),
      );
  };

  render() {
    const { currentUser, intl } = this.props;
    const {
      loadingAnalyzerFields,
      loadingAnalyzerFieldsError,
      filterOptions,
      provisionOptions,
      filters,
      recentQueries,
      savedQueries,
      savedQueriesCount,
      booleanQuery,
      loadingAnalyzerQueries,
      loadingAnalyzerQueriesError,
    } = this.state;

    const hasSearchV2Flag = testHasFlag(FeatureFlagType.SearchV2)(currentUser);

    return (
      <PageLayout
        title={intl.formatMessage({
          id: 'AnalyzerPage.pageTitle',
          defaultMessage: 'Analyzer',
          description: 'Page title for the analyzer page',
        })}
      >
        <Layout direction="row" spacing={8}>
          <AnalyzerFilters
            loading={loadingAnalyzerFields}
            loadingError={loadingAnalyzerFieldsError}
            filters={filters}
            filterOptions={filterOptions}
            provisionOptions={provisionOptions}
            loadAnalyzerFiltersData={this.loadAnalyzerFiltersData}
            updateFilters={(filters) => this.handleUpdateFilters(filters, true)}
            updateFilterOptions={this.handleUpdateFilterOptions}
          />
          <Layout direction="column" spacing={4} w="100%">
            <AnalyzerForm
              booleanQuery={booleanQuery}
              filters={filters}
              filterOptions={filterOptions}
              provisionOptions={provisionOptions}
              updateFilters={this.handleUpdateFilters}
              updateFilterOptions={this.handleUpdateFilterOptions}
              updateBooleanQuery={this.handleUpdateBooleanQuery}
              handleSubmit={this.handleSubmit}
            />
            <Layout>
              <ContentContainer
                loadingContent={{ isLoading: loadingAnalyzerQueries }}
                placeholderContent={
                  loadingAnalyzerQueriesError
                    ? {
                        action: {
                          text: intl.formatMessage({
                            id: 'AnalyzerPage.tryAgainButton',
                            defaultMessage: 'Try Again',
                            description:
                              'Button text to retry loading searches',
                          }),
                          level: 'primary',
                          icon: 'reload',
                          onClick: this.loadAnalyzerSearchQueries,
                        },
                        title: intl.formatMessage({
                          id: 'AnalyzerPage.loadSearchesFailedTitle',
                          defaultMessage: 'Failed to load searches.',
                          description:
                            'Title for error when searches fail to load',
                        }),
                        message: intl.formatMessage({
                          id: 'AnalyzerPage.loadSearchesFailedMessage',
                          defaultMessage:
                            'There was an error fetching your searches. Please try again.',
                          description:
                            'Error message when searches fail to load',
                        }),
                      }
                    : undefined
                }
              >
                {!hasSearchV2Flag && recentQueries.length > 0 ? (
                  <AnalyzersQueryTable
                    disableApplyBtn={!filterOptions.length}
                    sectionTitle={intl.formatMessage({
                      id: 'AnalyzerPage.recentSearchesTitle',
                      defaultMessage: 'Recent Searches',
                      description: 'Title for recent searches section',
                    })}
                    sectionSubtitle={intl.formatMessage({
                      id: 'AnalyzerPage.recentSearchesSubtitle',
                      defaultMessage:
                        "These are some recent searches you've run.",
                      description: 'Subtitle for recent searches section',
                    })}
                    sectionIcon={<ExpiringIcon size="32" color={evisortBlue} />}
                    nameColumnHeader={intl.formatMessage({
                      id: 'AnalyzerPage.filtersColumnHeader',
                      defaultMessage: 'FILTERS',
                      description:
                        'Column header for filters in recent searches table',
                    })}
                    dateColumnHeader={intl.formatMessage({
                      id: 'AnalyzerPage.dateOfSearchColumnHeader',
                      defaultMessage: 'DATE OF SEARCH',
                      description:
                        'Column header for date of search in recent searches table',
                    })}
                    listItems={recentQueries}
                    applyFilters={(recentSearches) =>
                      this.handleUpdateFiltersFromTableQuery(
                        recentSearches,
                        true,
                      )
                    }
                    searchFilters={this.handleSearchFilters}
                  />
                ) : null}
                {!hasSearchV2Flag && (
                  <AnalyzersQueryTable
                    disableApplyBtn={!filterOptions.length}
                    sectionTitle={intl.formatMessage({
                      id: 'AnalyzerPage.savedSearchesTitle',
                      defaultMessage: 'Saved Searches',
                      description: 'Title for saved searches section',
                    })}
                    sectionSubtitle={intl.formatMessage({
                      id: 'AnalyzerPage.savedSearchesSubtitle',
                      defaultMessage:
                        'Re–run one of your previously saved searches.',
                      description: 'Subtitle for saved searches section',
                    })}
                    sectionIcon={<InboxIcon size="32" color={evisortBlue} />}
                    nameColumnHeader={intl.formatMessage({
                      id: 'AnalyzerPage.nameColumnHeader',
                      defaultMessage: 'NAME',
                      description:
                        'Column header for name in saved searches table',
                    })}
                    dateColumnHeader={intl.formatMessage({
                      id: 'AnalyzerPage.dateCreatedColumnHeader',
                      defaultMessage: 'DATE CREATED',
                      description:
                        'Column header for date created in saved searches table',
                    })}
                    listItems={savedQueries}
                    count={savedQueriesCount}
                    applyFilters={(recentSearches) =>
                      this.handleUpdateFiltersFromTableQuery(
                        recentSearches,
                        true,
                      )
                    }
                    searchFilters={this.handleSearchFilters}
                    renameQuery={this.handleQueryRename}
                    deleteQuery={this.handleQueryDelete}
                    onPageChange={this.handleSavedSearchesPageChange}
                    savedSearches
                  />
                )}
              </ContentContainer>
            </Layout>
          </Layout>
        </Layout>
      </PageLayout>
    );
  }
}

const mapStateToProps = ({ currentUser, analyzer }) => ({
  currentUser,
  filters: analyzer.filters,
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        analyzerSetData: slices.analyzer.actions.setData,
        analyzerResetData: slices.analyzer.actions.resetData,
      },
      dispatch,
    ),
  };
}
Page = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouting(injectIntl(Page)));

export default withFlags(withFormatDate(Page));
